import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '@/lib/db/db';
import { useLiveQuery } from 'dexie-react-hooks';
import { Search, ChevronRight, BookOpen, Lock } from 'lucide-react';
import { cn } from '@/lib/utils';

// New UI System
import { PageShell } from '@/components/ui/PageShell';
import { EliteCard } from '@/components/ui/EliteCard';

export function DrillsPage() {
    const navigate = useNavigate();
    const [search, setSearch] = useState('');
    const [filter, setFilter] = useState<'all' | 'neutral' | 'top' | 'bottom'>('all');

    const drills = useLiveQuery(() => db.drills.toArray()) ?? [];

    const filtered = drills.filter(d => {
        const matchesSearch = d.name.toLowerCase().includes(search.toLowerCase()) ||
            (d.category?.toLowerCase().includes(search.toLowerCase()) ?? false);
        const matchesFilter = filter === 'all' || d.position === filter;
        return matchesSearch && matchesFilter;
    });

    const categoryColors: Record<string, string> = {
        neutral: 'border-l-blue-500',
        top: 'border-l-orange-500',
        bottom: 'border-l-red-500',
        conditioning: 'border-l-green-500',
        defense: 'border-l-purple-500',
    };

    return (
        <PageShell
            title="Drill Library"
            subtitle={`${filtered.length} techniques available`}
            right={
                <div className="bg-bg-elevated border border-white/10 rounded-full px-3 py-1 flex items-center gap-2">
                    <BookOpen className="w-3 h-3 text-gold-500" />
                    <span className="text-[10px] font-bold text-white uppercase tracking-widest">{filter} Mode</span>
                </div>
            }
        >
            <div className="space-y-6 animate-fade-in">

                {/* Search */}
                <div className="relative group">
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted group-focus-within:text-gold-500 transition-colors" />
                    <input
                        type="search"
                        placeholder="SEARCH DRILLS..."
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        className="w-full bg-bg-surface border border-white/10 rounded-none pl-11 pr-4 py-4 text-sm font-bold text-white focus:outline-none focus:border-gold-500/50 focus:bg-white/5 transition-all shadow-[0_4px_20px_rgba(0,0,0,0.4)] placeholder:text-text-muted placeholder:font-black placeholder:tracking-widest"
                    />
                </div>

                {/* Filters (Sharp, not bubbly) */}
                <div className="flex gap-2 overflow-x-auto pb-2 no-scrollbar">
                    {(['all', 'neutral', 'top', 'bottom'] as const).map(f => (
                        <button
                            key={f}
                            onClick={() => setFilter(f)}
                            className={cn(
                                "px-4 py-2 font-black uppercase tracking-widest text-[10px] border transition-all whitespace-nowrap shadow-sm",
                                filter === f
                                    ? "bg-gold-500/10 border-gold-500 text-gold-400"
                                    : "bg-bg-surface border-white/10 text-text-muted hover:border-white/30 hover:text-white"
                            )}
                        >
                            {f.charAt(0).toUpperCase() + f.slice(1)}
                        </button>
                    ))}
                </div>

                {/* Drill List */}
                <div className="grid gap-3">
                    {filtered.length > 0 ? (
                        filtered.map((d, i) => {
                            // Mocking pro/free status for UI demonstration
                            const isPro = i % 3 === 0;

                            return (
                                <EliteCard
                                    key={d.id}
                                    className={cn(
                                        "flex flex-col sm:flex-row items-start sm:items-center gap-4 border-l-4 py-4 px-5 cursor-pointer shadow-[0_4px_20px_rgba(0,0,0,0.4)] animate-fade-in-up hover:bg-white/[0.02]",
                                        categoryColors[d.position] || 'border-l-white/10'
                                    )}
                                >
                                    <div
                                        className="flex-1 w-full flex items-start justify-between"
                                        onClick={() => navigate(`/drills/${d.id}`)}
                                    >
                                        <div className="flex-1 pr-4">
                                            <div className="flex items-center gap-2 mb-1">
                                                <h3 className="text-white font-bold text-base md:text-lg leading-tight group-hover:text-gold-100 transition-colors">
                                                    {d.name}
                                                </h3>
                                                {isPro ? (
                                                    <span className="px-1.5 py-0.5 bg-black text-[8px] font-black uppercase tracking-widest text-text-muted border border-white/10 flex items-center gap-1 shrink-0">
                                                        <Lock className="w-2 h-2" /> PRO
                                                    </span>
                                                ) : (
                                                    <span className="px-1.5 py-0.5 bg-gold-500/10 text-[8px] font-black uppercase tracking-widest text-gold-500 border border-gold-500/20 shrink-0">
                                                        FREE
                                                    </span>
                                                )}
                                            </div>
                                            <p className="text-[10px] text-text-muted uppercase tracking-widest font-bold line-clamp-1">
                                                {d.category || 'Fundamentals'}
                                            </p>
                                        </div>

                                        <div className="w-8 h-8 rounded-full bg-white/5 border border-white/10 flex items-center justify-center shrink-0 mt-1 sm:mt-0 transition-all group-hover:bg-gold-500/10 group-hover:border-gold-500/30 group-hover:text-gold-400">
                                            <ChevronRight className="w-4 h-4 text-text-muted group-hover:text-gold-400" />
                                        </div>
                                    </div>
                                </EliteCard>
                            );
                        })
                    ) : (
                        <div className="py-12 flex flex-col items-center justify-center text-center border border-white/5 bg-bg-surface/50 p-6 shadow-inset">
                            <BookOpen className="w-12 h-12 text-white/10 mb-4" />
                            <h3 className="text-white font-black uppercase tracking-tight text-lg mb-1">No drills found</h3>
                            <p className="text-text-muted text-sm max-w-[250px] mx-auto mb-6">
                                Try adjusting your search or filters to find what you're looking for.
                            </p>
                            <button
                                onClick={() => { setSearch(''); setFilter('all'); }}
                                className="text-[10px] font-black uppercase tracking-widest text-gold-400 border border-gold-500/30 px-4 py-2 hover:bg-gold-500/10 transition-colors"
                            >
                                Clear All Filters
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </PageShell>
    );
}
